import { useCallback, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { Section, SECTION_TYPE } from 'atoms/Section';
import { cvtDatetime } from 'commons/utils';
import { type Audition } from 'hooks/u005/useGetAuditionHooks';
import { DeleteApplicantDialog } from './DeleteApplicantDialog';
import { DeclineConfirmDialog } from './DeclineConfirmDialog';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { SpecificUseConsent } from 'pages/U005/SpecificUseConsent';
import { PrivacyPolicyConsent } from 'pages/U005/PrivacyPolicyConsent';
import { AuditionTermsConsent } from 'pages/U005/AuditionTermsConsent';
import { EXAMINATION_KBN } from 'commons/auditionInfo';

type Props = {
  userId: string;
  auditionId: string;
  audition: Audition;
  loading: boolean;
  specific_use: string;
  privacy_notice: string;
  audition_terms: string;
  now_dtm: Date;
  declineKbn: string;
  examination_no: number;
  examination_kbn: string;
  examination_s_no: number;
  result: string;
};

export const AuditionMessageSection = (props: Props) => {
  const [open, setOpen] = useState(false);
  const { t: tAuditionInfo } = useTranslationWithLang('auditionInfo');
  const [specificUseOpen, setSpecificUseOpen] = useState<boolean>(false);

  const [isOpenDeclineConfirmDialog, setIsOpenDeclineConfirmDialog] = useState<boolean>(false);
  const [updateDeclineKbn, setUpdateDeclineKbn] = useState<string>('');

  const onOkDeclineConfirmDialog = useCallback((declineKbn: string) => {
    setUpdateDeclineKbn(declineKbn);
    setIsOpenDeclineConfirmDialog(false);
    setOpen(true);
  }, []);

  const endDate = new Date(props.audition.examination_info?.end_dtm);
  const afterExaminationDtm = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate() + 1,
    0,
    0,
    0
  );
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState<boolean>(false);
  const [auditionTermsOpen, setAuditionTermsOpen] = useState<boolean>(false);

  return (
    <>
      {specificUseOpen && (
        <SpecificUseConsent
          auditionId={props.auditionId}
          onComplete={() => setSpecificUseOpen(false)}
        />
      )}
      {auditionTermsOpen && (
        <AuditionTermsConsent
          auditionId={props.auditionId}
          onComplete={() => setAuditionTermsOpen(false)}
        />
      )}
      {privacyPolicyOpen && (
        <PrivacyPolicyConsent
          auditionId={props.auditionId}
          onComplete={() => setPrivacyPolicyOpen(false)}
        />
      )}
      <Section type={SECTION_TYPE.AUDITION_INFO}>
        <Stack spacing={1} className="h-full p-4 overflow-y-auto overflow-x-hidden">
          <div className="flex flex-col justify-between h-full w-full">
            {(props.declineKbn === '1' || props.declineKbn === '2') && (
              <div className="flex flex-col justify-between h-full w-full">
                <p className="text-[--c-error] whitespace-pre-wrap">{tAuditionInfo('TU005_054')}</p>
              </div>
            )}
            {/* 審査コメント */}
            {props.audition.examination_info?.examination_kbn !== EXAMINATION_KBN.DOCUMENT &&
            props.audition.examination_info?.confirmed === '0' &&
            props.audition.examination_info?.examination_no === 1 ? (
              <div className="whitespace-pre-wrap break-words">{tAuditionInfo('TU005_049')}</div>
            ) : (
              (props.audition.examination_info?.examination_kbn === EXAMINATION_KBN.DOCUMENT ||
                (props.audition.examination_info?.examination_kbn !== EXAMINATION_KBN.DOCUMENT &&
                  afterExaminationDtm < props.now_dtm)) && (
                <div className="whitespace-pre-wrap break-words">
                  {props.audition.examination_info?.mypage_txt}
                </div>
              )
            )}

            {/* 審査案内 */}
            {props.audition.examination_info?.examination_kbn !== EXAMINATION_KBN.DOCUMENT &&
              afterExaminationDtm >= props.now_dtm &&
              !(
                props.audition.examination_info?.examination_kbn !== EXAMINATION_KBN.DOCUMENT &&
                props.audition.examination_info?.confirmed === '0' &&
                props.audition.examination_info?.examination_no === 1
              ) && (
                <>
                  {/* ○次審査 */}
                  <div className="font-bold whitespace-nowrap">
                    {props.audition.examination_info?.examination_no} {tAuditionInfo('TU005_008')}
                  </div>
                  {/* 会場日時 */}
                  <div className="flex flex-col w-full mt-4">
                    <div className="select-none whitespace-nowrap">
                      {tAuditionInfo('TU005_010')}:
                    </div>
                    {props.audition.examination_info?.start_dtm != null &&
                      props.audition.examination_info?.end_dtm != null && (
                        <div className="ml-2 flex flex-wrap w-full">
                          <span className="whitespace-nowrap mr-1">
                            {cvtDatetime(new Date(props.audition.examination_info?.start_dtm))}
                          </span>
                          <span className="whitespace-nowrap">
                            <span className="mx-1">〜</span>
                            {cvtDatetime(new Date(props.audition.examination_info?.end_dtm))}
                          </span>
                        </div>
                      )}
                  </div>
                  {/* 集合時間 */}
                  <div className="flex mt-4">
                    <div className="select-none whitespace-nowrap">
                      {tAuditionInfo('TU005_041')}:
                    </div>
                    <div className="ml-2 break-all">
                      {tAuditionInfo('TU005_042', {
                        meeting_time: new Date(props.audition.examination_info?.meeting_time)
                          .toLocaleTimeString('ja-JP', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })
                          .replace(/\//g, '.'),
                      })}
                    </div>
                  </div>
                  {/* 会場情報 */}
                  <div className="flex flex-col w-full mt-4">
                    <div className="select-none whitespace-nowrap font-medium">
                      {tAuditionInfo('TU005_009')}:
                    </div>
                    <div className="ml-4 break-all">
                      {props.audition.examination_info?.examination_kbn ===
                      EXAMINATION_KBN.REMOTE_INTERVIEW ? (
                        <div>
                          <div>
                            {tAuditionInfo('TU005_043')} (
                            <a
                              href={props.audition.examination_info?.examination_venue}
                              className="underline text-[--c-textinput-label]"
                            >
                              {tAuditionInfo('TU005_047')}
                            </a>
                            )
                          </div>
                          <div className="text-[--c-text2]">{tAuditionInfo('TU005_046')}</div>
                        </div>
                      ) : (
                        props.audition.examination_info?.examination_venue
                      )}
                    </div>
                  </div>
                  {/* 会場注意事項 */}
                  {props.audition.examination_info?.examination_kbn !==
                    EXAMINATION_KBN.REMOTE_INTERVIEW && (
                    <div className="flex flex-col w-full mt-4">
                      <div className="select-none whitespace-nowrap">
                        {tAuditionInfo('TU005_039')}:
                      </div>
                      <div className="ml-2 break-words">{tAuditionInfo('TU005_040')}</div>
                    </div>
                  )}
                  {/* 審査番号
                    <div className="flex mt-4">
                      <div>{tAuditionInfo('TU005_011')}</div>
                      <div className="ml-1">
                        {props.audition.examination_info?.examination_sort_no}
                      </div>
                    </div> */}
                </>
              )}
            <div className="flex justify-end mt-auto w-full">
              <div className="flex flex-col items-end whitespace-nowrap">
                {props.specific_use === '1' && (
                  <Button
                    style={{ color: 'var(--c-textinput-label)', padding: '0px' }}
                    onClick={() => setSpecificUseOpen(true)}
                  >
                    {tAuditionInfo('TU005_025')}&nbsp;&gt;
                  </Button>
                )}
                {props.privacy_notice === '1' && (
                  <Button
                    style={{ color: 'var(--c-textinput-label)', padding: '0px' }}
                    onClick={() => setPrivacyPolicyOpen(true)}
                  >
                    {tAuditionInfo('TU005_028')}&nbsp;&gt;
                  </Button>
                )}
                {props.audition_terms === '1' && (
                  <Button
                    style={{ color: 'var(--c-textinput-label)', padding: '0px' }}
                    onClick={() => setAuditionTermsOpen(true)}
                  >
                    {tAuditionInfo('TU005_029')}&nbsp;&gt;
                  </Button>
                )}
                {(props.declineKbn === '0' || props.declineKbn === '4') && (
                  <Button
                    style={{ color: 'var(--c-textinput-label)', padding: '0px' }}
                    onClick={() => setIsOpenDeclineConfirmDialog(true)}
                  >
                    {tAuditionInfo('TU005_007')}&nbsp;&gt;
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Stack>
      </Section>
      <DeclineConfirmDialog
        open={isOpenDeclineConfirmDialog}
        onOk={onOkDeclineConfirmDialog}
        onClose={() => setIsOpenDeclineConfirmDialog(false)}
        examination_no={props.examination_no}
        result={props.result}
      />
      <DeleteApplicantDialog
        open={open}
        userId={props.userId}
        auditionId={props.auditionId}
        auditionName={props.audition.audition_info.title}
        reason={props.audition.reason}
        declineKbn={updateDeclineKbn}
        examinationNo={props.examination_no}
        examinationKbn={props.examination_kbn}
        examination_s_no={props.examination_s_no}
        result={props.result}
        onClose={() => setOpen(false)}
      ></DeleteApplicantDialog>
    </>
  );
};
